import type { Reservation, ReservationCreate, ReservationUpdate } from "@/types";
import { apiClient } from "./client";

export const reservationService = {
  list: async (
    propertyId: string,
    range?: { from: string; to: string }
  ): Promise<Reservation[]> => {
    const { data } = await apiClient.get<Reservation[]>("/admin/reservations", {
      params: { property_id: propertyId, ...range },
    });
    return data;
  },

  create: async (body: ReservationCreate): Promise<Reservation> => {
    const { data } = await apiClient.post<Reservation>("/admin/reservations", body);
    return data;
  },

  /** Move/edit a booked reservation (dates, room, type, guest name). */
  update: async (id: string, body: ReservationUpdate): Promise<Reservation> => {
    const { data } = await apiClient.patch<Reservation>(`/admin/reservations/${id}`, body);
    return data;
  },

  /** room_id omitted → the API falls back to the pre-assigned room. */
  checkIn: async (id: string, room_id?: string): Promise<Reservation> => {
    const { data } = await apiClient.post<Reservation>(
      `/admin/reservations/${id}/check-in`,
      room_id ? { room_id } : {}
    );
    return data;
  },

  /** Pre-assign (or clear, with null) a room before check-in. */
  assignRoom: async (id: string, room_id: string | null): Promise<Reservation> => {
    const { data } = await apiClient.post<Reservation>(`/admin/reservations/${id}/assign-room`, {
      room_id,
    });
    return data;
  },

  checkOut: async (id: string): Promise<Reservation> => {
    const { data } = await apiClient.post<Reservation>(`/admin/reservations/${id}/check-out`);
    return data;
  },

  cancel: async (id: string): Promise<Reservation> => {
    const { data } = await apiClient.post<Reservation>(`/admin/reservations/${id}/cancel`);
    return data;
  },

  /** 게스트에게 안내 링크 메일을 (재)발송한다. 결과는 guest.welcome_* 로 돌아온다. */
  sendWelcome: async (id: string): Promise<Reservation> => {
    const { data } = await apiClient.post<Reservation>(`/admin/reservations/${id}/send-welcome`);
    return data;
  },

  /** Issue (or return the existing) guest link for a reservation. Idempotent. */
  issueGuest: async (id: string): Promise<Reservation> => {
    const { data } = await apiClient.post<Reservation>(`/admin/reservations/${id}/issue-guest`);
    return data;
  },
};
